import * as dat from 'dat.gui';
import GSAP from 'gsap';

import { CONFIG, box } from './Gallery/layout';

// Owns the intro timeline (autoplays on load) and, behind #debug, the layout
// authoring tools. The intro animates each item's `tf` (offset layer); authoring
// edits the item's `override` (the resting destination, in design space) and
// re-bakes via Gallery.applyLayout().
//
//   - intro: deck -> spread -> grid + camera zoom + lens bend
//   - #debug: click-to-select, drag-to-place (writes override), snap, zoom,
//     overlap guard, and Export (serialises ITEMS with overrides for layout.js)
export default class GalleryControls {
	constructor({ gallery, items, camera, uniforms, galleryComponent }) {
		this.gallery = gallery || null;
		this.galleryComponent = galleryComponent || null;
		this.items = items || [];
		this.camera = camera || null;
		this.uniforms = uniforms || null; // shared { uLensK }
		this.active = window.location.hash === '#debug';
		this.gap = CONFIG.gap;
		if (!this.items.length) return;

		this.selected = 0;
		this._drag = null;
		this._opts = { snap: false, zoom: 1 };

		this.buildTimeline();

		if (this.active) {
			// jump to the resting grid so authored positions are true (no intro mid-tween)
			this.timeline.progress(1).pause();
			this.freezeForAuthoring();
			this.buildGui();
			this.buildBar();
			this.buildAuthoring();
		} else {
			this.timeline.play(); // autoplay intro for visitors
		}
	}

	// World-space resting center of a plane (set by Gallery.applyLayout). tf=0 lands here.
	baseWorld(item) {
		return { x: item.baseX, y: item.baseY };
	}

	// ---- Intro choreography: deck -> spread -> grid (tf=0 == the resting spot) ----
	buildTimeline() {
		const n = this.items.length;
		const rowY = 0;
		const step = 80;
		const mid = (n - 1) / 2;

		const SPREAD = 0;
		const SPREAD_DUR = 0.9;
		const GRID = 1.1;
		const GRID_DUR = 1.4;

		const tl = GSAP.timeline({ paused: true, onUpdate: () => this.syncBar() });

		const cam = this.camera && this.camera.el;
		if (cam) {
			const lift = (this.items[0].sizes.height || window.innerHeight) * 0.18;
			const camDur = GRID_DUR + (n - 1) * 0.02;

			cam.zoom = 0.6;
			cam.position.y = lift;
			cam.updateProjectionMatrix();

			tl.fromTo(
				cam,
				{ zoom: 0.6 },
				{
					zoom: 1,
					duration: camDur,
					ease: 'power2.inOut',
					immediateRender: true,
					onUpdate: () => cam.updateProjectionMatrix(),
				},
				GRID
			);
			tl.fromTo(
				cam.position,
				{ y: lift },
				{ y: 0, duration: camDur, ease: 'power2.inOut', immediateRender: true },
				GRID
			);
		}

		if (this.uniforms) {
			const lensDur = GRID_DUR + (n - 1) * 0.02;
			this.uniforms.uLensK.value = 0;
			tl.to(
				this.uniforms.uLensK,
				{ value: 0.0222, duration: lensDur, ease: 'power2.inOut' },
				GRID
			);
		}

		const rand = (a) => (Math.random() * 2 - 1) * a;
		const JIT_X = 60;
		const JIT_Y = 90;
		const JIT_ROT = 0.14;
		const JIT_Z = 0.4;

		this.items.forEach((item, i) => {
			const base = this.baseWorld(item);
			// resting transform = authored rotation/scale (the tf channel is the only
			// rot/scale path), so the grid lands on the authored look, not flat.
			const o = item.item && item.item.override;
			const restRot = (o && o.rotation) || 0;
			const restScale = (o && o.scale) || 1;

			const jx = rand(JIT_X);
			const jy = rand(JIT_Y);
			const jr = rand(JIT_ROT);
			const jz = i * 0.01 + rand(JIT_Z);

			Object.assign(item.tf, {
				x: -base.x + jx,
				y: rowY - base.y + jy,
				z: jz,
				rotation: jr,
				scale: 1,
			});

			tl.to(
				item.tf,
				{
					x: (i - mid) * step - base.x + jx,
					y: rowY - base.y + jy,
					duration: SPREAD_DUR,
					ease: 'power2.inOut',
				},
				SPREAD
			);

			tl.to(
				item.tf,
				{
					x: 0,
					y: 0,
					z: 0,
					rotation: restRot,
					scale: restScale,
					duration: GRID_DUR,
					ease: 'power3.inOut',
				},
				GRID + i * 0.02
			);
		});

		this.timeline = tl;
	}

	// ---- dat.GUI ----
	buildGui() {
		this.gui = new dat.GUI({ width: 320 });

		this._sel = { index: 0 };
		this.gui
			.add(this._sel, 'index', 0, this.items.length - 1, 1)
			.name('plane')
			.onChange((v) => this.select(v | 0));

		// x/y edit the override (design-space center) + re-bake; w/h edit the cell
		// spans on the layout item (the box, in cells) + re-bake; rot/scale ride tf live.
		this._proxy = { x: 0, y: 0 };
		const f = this.gui.addFolder('Transform (selected)');
		const sel = this.items[this.selected];
		this._ctl = {
			x: f.add(this._proxy, 'x', -6000, 6000, 1).name('pos x').onChange(() => this.applyProxy()),
			y: f.add(this._proxy, 'y', -6000, 6000, 1).name('pos y').onChange(() => this.applyProxy()),
			w: f.add(sel.item, 'w', 1, 14, 1).name('width (cells)').onChange(() => this.applySpan()),
			h: f.add(sel.item, 'h', 1, 14, 1).name('height (cells)').onChange(() => this.applySpan()),
			rotation: f
				.add(sel.tf, 'rotation', -Math.PI, Math.PI, 0.001)
				.name('rotZ (rad)')
				.onChange((v) => this.mirrorTf('rotation', v)),
			scale: f
				.add(sel.tf, 'scale', 0.05, 3, 0.01)
				.name('scale')
				.onChange((v) => this.mirrorTf('scale', v)),
		};
		f.open();
		this.select(0);

		const a = this.gui.addFolder('Authoring');
		a.add(this._opts, 'snap').name('snap to grid');
		a.add(this._opts, 'zoom', 0.2, 1.5, 0.01)
			.name('camera zoom')
			.onChange((v) => this.setZoom(v));
		const acts = {
			matchAspect: () => this.matchAspect(),
			resetSelected: () => this.resetSelected(),
			export: () => this.exportItems(),
			logSelected: () => console.log(`override[${this.selected}]`, this.items[this.selected].item.override),
		};
		Object.keys(acts).forEach((k) => a.add(acts, k));
		a.open();

		const t = this.gui.addFolder('Intro');
		const tacts = {
			play: () => this.timeline.play(),
			pause: () => this.timeline.pause(),
			restart: () => this.timeline.restart(),
			toGrid: () => this.timeline.progress(1).pause(),
		};
		Object.keys(tacts).forEach((k) => t.add(tacts, k));

		if (this.uniforms) {
			const l = this.gui.addFolder('Lens');
			l.add(this.uniforms.uLensK, 'value', -0.6, 0.6, 0.001).name('bend (uLensK)');
			l.open();
		}
	}

	// seed an override at the plane's current resting spot (design-space center)
	seedOverride(item) {
		if (item.item.override) return;
		const scale = (this.galleryComponent && this.galleryComponent.scale) || 1;
		const P = this.gallery.period;
		item.item.override = {
			x: item.baseX / scale + P.w / 2,
			y: -item.baseY / scale + P.h / 2,
			rotation: item.tf.rotation || 0,
			scale: item.tf.scale || 1,
		};
	}

	// Entering #debug: pin the period to the current field extent and give EVERY
	// plane an override at its current spot. Then editing one plane's size/pos only
	// changes that plane — no row repack, no period change, no global shift.
	freezeForAuthoring() {
		if (!this.gallery.period) return;
		this.gallery.lockedPeriod = {
			w: this.gallery.period.w,
			h: this.gallery.period.h,
		};
		this.items.forEach((it) => this.seedOverride(it));
		this.gallery.applyLayout();
	}

	select(i) {
		this.selected = i;
		const item = this.items[i];
		this.seedOverride(item);
		// repoint controllers at this item: rot/scale on tf, w/h on the layout item
		this._ctl.rotation.object = item.tf;
		this._ctl.scale.object = item.tf;
		this._ctl.w.object = item.item;
		this._ctl.h.object = item.item;
		this.syncProxy();
		Object.values(this._ctl).forEach((c) => c.updateDisplay());
		if (this._sel) {
			this._sel.index = i;
		}
	}

	// push the selected override's x/y into the GUI proxy
	syncProxy() {
		const o = this.items[this.selected].item.override;
		if (o && this._proxy) {
			this._proxy.x = Math.round(o.x);
			this._proxy.y = Math.round(o.y);
		}
	}

	applyProxy() {
		const o = this.items[this.selected].item.override;
		if (!o) return;
		o.x = this._proxy.x;
		o.y = this._proxy.y;
		this.gallery.applyLayout();
	}

	// w/h sliders changed the cell spans on the layout item — re-bake the box.
	applySpan() {
		this.gallery.applyLayout();
		this.syncProxy();
	}

	// Set the selected plane's cell spans so the box matches the image aspect
	// (keeps the current width, fits the height) — fixes a portrait image cropped
	// inside a wide box, and vice-versa via the width slider.
	matchAspect() {
		const item = this.items[this.selected];
		let aspect;
		if (item.isVideo) {
			const v = document.querySelector('.portfolio__video');
			aspect = v && v.videoWidth ? v.videoWidth / v.videoHeight : null;
		} else {
			const meta = this.gallery.info && this.gallery.info.get(item.item.src);
			aspect = meta && meta.aspect;
		}
		if (!aspect) return;
		const { cell, gap } = CONFIG;
		const unbox = (px) => (px + gap) / (cell + gap); // px -> cells (inverse of box)
		const targetH = box(item.item.w) / aspect; // want box(w)/box(h) == aspect
		item.item.h = Math.max(1, Math.round(unbox(targetH)));
		this.gallery.applyLayout();
		this.select(this.selected); // refresh the w/h sliders
	}

	mirrorTf(key, v) {
		// rotation/scale live on tf (visible immediately) + mirror to override for export
		const item = this.items[this.selected];
		this.seedOverride(item);
		item.item.override[key] = v;
		item.tf[key] = v;
	}

	// reset the transform (rotation/scale) only — keep position + size so the plane
	// stays pinned (clearing the override would unfreeze it and shift the field).
	resetSelected() {
		const item = this.items[this.selected];
		this.seedOverride(item);
		item.item.override.rotation = 0;
		item.item.override.scale = 1;
		item.tf.rotation = 0;
		item.tf.scale = 1;
		this.gallery.applyLayout();
		this.select(this.selected); // refresh GUI
	}

	setZoom(v) {
		if (!this.camera) return;
		this.camera.el.zoom = v;
		this.camera.el.updateProjectionMatrix();
	}

	// ---- click-to-select + drag-to-place (window capture so it pre-empts pan) ----
	buildAuthoring() {
		this._hl = document.createElement('div');
		Object.assign(this._hl.style, {
			position: 'fixed',
			border: '2px solid #34c759',
			borderRadius: '4px',
			pointerEvents: 'none',
			zIndex: 99998,
			left: '-9999px',
			top: '0',
		});
		document.body.appendChild(this._hl);

		this._onDown = (e) => {
			const mx = e.clientX;
			const my = e.clientY;
			const i = this.pick(mx, my);
			if (i < 0) return; // empty space → let the drag-surface pan
			e.stopPropagation();
			e.preventDefault();
			this.select(i);
			this._drag = { i, x: mx, y: my };
		};
		this._onMove = (e) => {
			if (!this._drag) return;
			e.stopPropagation();
			const scale = (this.galleryComponent && this.galleryComponent.scale) || 1;
			const zoom = (this.camera && this.camera.el.zoom) || 1;
			const o = this.items[this._drag.i].item.override;
			o.x += (e.clientX - this._drag.x) / (zoom * scale);
			o.y += (e.clientY - this._drag.y) / (zoom * scale);
			if (this._opts.snap) {
				const stepX = CONFIG.cell + CONFIG.gap;
				o.x = Math.round(o.x / stepX) * stepX;
				o.y = Math.round(o.y / stepX) * stepX;
			}
			this._drag.x = e.clientX;
			this._drag.y = e.clientY;
			this.gallery.applyLayout();
			this.syncProxy();
			Object.values(this._ctl).forEach((c) => c.updateDisplay());
		};
		this._onUp = () => {
			this._drag = null;
		};

		window.addEventListener('mousedown', this._onDown, true);
		window.addEventListener('mousemove', this._onMove, true);
		window.addEventListener('mouseup', this._onUp, true);
	}

	// CPU hit-test: replicate the shader place() (wrap + ortho + zoom), ignore the
	// gentle lens. Returns the topmost (highest draw index) plane under (mx,my).
	screenRect(item) {
		const gc = this.galleryComponent;
		const scale = (gc && gc.scale) || 1;
		const zoom = (this.camera && this.camera.el.zoom) || 1;
		const P = this.gallery.period;
		const Pw = P.w * scale;
		const Ph = P.h * scale;
		const sc = gc ? gc.scroll : { x: 0, y: 0 };
		const wrap = (v, p) => (p ? ((((v + p / 2) % p) + p) % p) - p / 2 : v);
		const cx = wrap(item.baseX + sc.x * scale, Pw);
		const cy = wrap(item.baseY - sc.y * scale, Ph);
		const vw = this.gallery.sizes.width;
		const vh = this.gallery.sizes.height;
		return {
			sx: vw / 2 + cx * zoom,
			sy: vh / 2 - cy * zoom,
			sw: item.boxW * zoom,
			sh: item.boxH * zoom,
		};
	}

	pick(mx, my) {
		for (let i = this.items.length - 1; i >= 0; i--) {
			const r = this.screenRect(this.items[i]);
			if (Math.abs(mx - r.sx) <= r.sw / 2 && Math.abs(my - r.sy) <= r.sh / 2) {
				return i;
			}
		}
		return -1;
	}

	// toroidal AABB: true if the selected plane is within `gap` of any other
	overlaps(item) {
		const scale = (this.galleryComponent && this.galleryComponent.scale) || 1;
		const P = this.gallery.period;
		const wrap = (v, p) => ((((v + p / 2) % p) + p) % p) - p / 2;
		const ax = item.baseX / scale + P.w / 2;
		const ay = -item.baseY / scale + P.h / 2;
		const aw = item.boxW / scale;
		const ah = item.boxH / scale;
		return this.items.some((o) => {
			if (o === item) return false;
			const ox = o.baseX / scale + P.w / 2;
			const oy = -o.baseY / scale + P.h / 2;
			const dx = Math.abs(wrap(ax - ox, P.w));
			const dy = Math.abs(wrap(ay - oy, P.h));
			return (
				dx < (aw + o.boxW / scale) / 2 + this.gap &&
				dy < (ah + o.boxH / scale) / 2 + this.gap
			);
		});
	}

	// called from Experience.update while #debug — track the selection highlight
	updateDebug() {
		if (!this.active || !this._hl) return;
		const item = this.items[this.selected];
		if (!item) return;
		const r = this.screenRect(item);
		const hl = this._hl;
		hl.style.width = `${r.sw}px`;
		hl.style.height = `${r.sh}px`;
		hl.style.left = `${r.sx - r.sw / 2}px`;
		hl.style.top = `${r.sy - r.sh / 2}px`;
		hl.style.borderColor = this.overlaps(item) ? '#ff3b30' : '#34c759';
	}

	// serialise ITEMS (with current overrides) for pasting into layout.js
	exportItems() {
		const r = (v) => Math.round(v * 10) / 10;
		const lines = this.items.map((it) => {
			const m = it.item;
			const o = m.override;
			const ov = o
				? `{ x: ${r(o.x)}, y: ${r(o.y)}, rotation: ${Math.round((o.rotation || 0) * 1000) / 1000}, scale: ${Math.round((o.scale || 1) * 100) / 100} }`
				: 'null';
			const vid = m.isVideo ? ' isVideo: true,' : '';
			return `\t{ id: '${m.id}', src: '${m.src}',${vid} w: ${m.w}, h: ${m.h}, mode: '${m.mode}', objectPosition: '${m.objectPosition}', override: ${ov} },`;
		});
		const text = `export const ITEMS = [\n${lines.join('\n')}\n];`;
		console.log(text);
		if (navigator.clipboard) navigator.clipboard.writeText(text).catch(() => {});
	}

	// ---- on-screen play bar ----
	buildBar() {
		const bar = document.createElement('div');
		bar.innerHTML = `
			<button data-act="play">▶</button>
			<button data-act="pause">❚❚</button>
			<button data-act="restart">⟲</button>
			<input type="range" min="0" max="1" step="0.001" value="1" />`;
		Object.assign(bar.style, {
			position: 'fixed',
			left: '50%',
			bottom: '24px',
			transform: 'translateX(-50%)',
			display: 'flex',
			gap: '8px',
			alignItems: 'center',
			padding: '8px 12px',
			background: 'rgba(0,0,0,0.7)',
			borderRadius: '8px',
			zIndex: 99999,
			font: '12px monospace',
			color: '#fff',
		});
		bar.querySelectorAll('button').forEach((b) =>
			Object.assign(b.style, {
				cursor: 'pointer',
				background: '#222',
				color: '#fff',
				border: '1px solid #444',
				borderRadius: '4px',
				padding: '4px 8px',
			})
		);

		this.scrub = bar.querySelector('input');
		this.scrub.style.width = '220px';

		bar.addEventListener('click', (e) => {
			const act = e.target.dataset.act;
			if (act === 'play') this.timeline.play();
			else if (act === 'pause') this.timeline.pause();
			else if (act === 'restart') this.timeline.restart();
		});
		this.scrub.addEventListener('input', () => {
			this.timeline.pause();
			this.timeline.progress(parseFloat(this.scrub.value));
		});

		document.body.appendChild(bar);
		this.bar = bar;
	}

	syncBar() {
		if (this.scrub) this.scrub.value = this.timeline.progress();
	}

	destroy() {
		this.timeline && this.timeline.kill();
		this.gui && this.gui.destroy();
		this.bar && this.bar.remove();
		if (this._hl) this._hl.remove();
		if (this._onDown) {
			window.removeEventListener('mousedown', this._onDown, true);
			window.removeEventListener('mousemove', this._onMove, true);
			window.removeEventListener('mouseup', this._onUp, true);
		}
	}
}
